import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { Button, FormControlLabel, Skeleton, Switch } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PageContainer from '../../components/PageContainer';
import { EmptyState, PageHeader, Panel, StatusChip } from '../../components/ui';
import StoriesTable from '../../components/StoriesTable';
import StoriesFilters from '../../components/StoriesFilters';
import { IStore, IOneStory } from '../../store/types';
import { Stories } from '../../store/slices';
import { useCan } from '../../auth/useCan';
import { STORIES_RW } from '../../auth/requirements';

const DEFAULT_PAGING_STATE = {
    perPage: 25,
    page: 1,
    total: 0,
};

/** Stories list: filters, bulk publish / gift cards, media previews. "Hide test data" filters the current page client-side. */
const StoriesPage = () => {
    const { stories, status, queryParams } = useSelector<IStore, { stories: IOneStory[]; status: string; queryParams: any }>((state) => ({
        stories: Object.values(state.stories.data) as any,
        status: state.stories.status,
        queryParams: state.stories.queryParams,
    }));
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const canCreate = useCan(STORIES_RW);
    const [searchParams] = useSearchParams();
    const authorId = searchParams.get('authorId');
    const [hideTestData, setHideTestData] = useState(true);

    const loadStories = (params?: any) => {
        dispatch<any>(Stories.getStories(params));
    };

    useEffect(() => {
        loadStories({
            ...queryParams,
            page: queryParams?.page || DEFAULT_PAGING_STATE.page,
            perPage: queryParams?.perPage || DEFAULT_PAGING_STATE.perPage,
            authorId,
        });
        // Reload only when the author in the URL changes; filters and paging reload on their own.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authorId]);

    const paging = { ...DEFAULT_PAGING_STATE, ...queryParams };
    const loading = status === 'loading';
    const visible = hideTestData ? stories.filter((story: any) => story.statusId !== 5) : stories;

    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="Customer Stories"
                title="Stories"
                badges={authorId ? <StatusChip tone="brand">Author #{authorId}</StatusChip> : undefined}
                meta={paging.total > 0 ? <span>{Number(paging.total).toLocaleString('en-US')} stories</span> : undefined}
                actions={(
                    <>
                        <FormControlLabel
                            sx={{ mr: 1 }}
                            control={<Switch checked={hideTestData} onChange={(event) => setHideTestData(event.target.checked)} />}
                            label="Hide test data"
                        />
                        {canCreate && (
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/stories/create')}>
                                Create story
                            </Button>
                        )}
                    </>
                )}
            />
            <Panel padding="none">
                <StoriesFilters queryParams={paging} loadStories={loadStories} loading={loading} />
                {loading && stories.length === 0 && (
                    <div style={{ padding: 16, display: 'grid', gap: 10 }} role="status" aria-label="Loading stories">
                        {[0, 1, 2, 3, 4, 5].map((row) => <Skeleton key={row} animation="wave" height={56} />)}
                    </div>
                )}
                {stories.length > 0 && (
                    <div style={{ position: 'relative', opacity: loading ? 0.5 : 1, transition: 'opacity .2s', pointerEvents: loading ? 'none' : 'auto' }} aria-busy={loading}>
                        <StoriesTable stories={visible} paging={paging} pagingUpdate={loadStories} />
                    </div>
                )}
                {!loading && stories.length === 0 && (
                    <EmptyState
                        title={status === 'rejected' ? 'Stories could not be loaded' : 'No stories match these filters'}
                        hint={status === 'rejected' ? 'The story service did not answer. Try again in a moment.' : 'Try another status or occasion, or clear the filters.'}
                        action={status === 'rejected'
                            ? <Button variant="outlined" size="small" onClick={() => loadStories(paging)}>Retry</Button>
                            : <Button variant="outlined" size="small" onClick={() => { dispatch(Stories.clearFilters()); loadStories(); }}>Clear filters</Button>}
                    />
                )}
                {!loading && stories.length > 0 && visible.length === 0 && (
                    <EmptyState title="Only test data on this page" hint="Turn off “Hide test data” or go to the next page." />
                )}
            </Panel>
        </PageContainer>
    );
};

export default StoriesPage;
